<template>
  <v-card class="project-status-card" elevation="0">
    <div v-if="selectedProject" class="status-avance-section">
      <div class="status-project-header">
        <v-icon small color="#2D4B95" class="mr-1">mdi-office-building-outline</v-icon>
        <span class="status-project-name">{{ selectedProject.name }}</span>
      </div>
    </div>

    <!-- Storage -->
    <v-card-text class="pa-3">
      <div class="mb-3">
        <div class="d-flex align-center mb-1">
          <v-icon small color="#2D4B95" class="mr-1">mdi-file-cog-outline</v-icon>
          <span class="caption font-weight-bold text-primary">Almacenamiento Utilizado</span>
        </div>
        <v-skeleton-loader
          type="list-item"
          v-if="$store.fileManagerStore.state.loadingStorageInfo"
        />
        <v-progress-linear
          v-else
          :value="storagePercent"
          rounded
          color="#2D4B95"
          background-color="#E0E0E0"
          height="16"
        >
          <template v-slot:default>
            <p class="white--text mb-0 font-weight-bold" style="font-size:11px;">
              {{ $store.fileManagerStore.state.projectStorageInfo.total_size | toDataSize }}
              <small>/10 GB</small>
            </p>
          </template>
        </v-progress-linear>
      </div>

      <v-divider class="mb-2" />

      <!-- Status board -->
      <div v-if="editingProjectBoard">
        <tiptap-vuetify
          v-model="projectStatus"
          :extensions="extensions"
          required
          id="edit-project-status"
        />
        <div class="d-flex justify-end mt-2" style="gap: 8px;">
          <v-btn
            rounded small text color="#2D4B95"
            :loading="loading"
            @click="editingProjectBoard = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            rounded small color="#5E85EF" dark
            :loading="loading"
            @click="updateBoard"
          >
            Actualizar
          </v-btn>
        </div>
      </div>

      <div v-else>
        <div class="d-flex align-center justify-space-between mb-1">
          <span class="caption font-weight-bold text-primary">
            <v-icon small color="#2D4B95" class="mr-1">mdi-clipboard-text-outline</v-icon>
            Estado del Proyecto
          </span>
          <v-btn
            text x-small
            v-if="checkCompanyLevelPermissions(['edit_project'])"
            @click="editProjectBoard"
          >
            <v-icon color="#5e85ef" x-small class="mr-1">mdi-note-edit</v-icon>
            <span class="caption text-primary text-capitalize">Editar</span>
          </v-btn>
        </div>
        <div
          class="status-board-info pa-2"
          v-if="selectedProject && selectedProject.status_board"
          v-html="selectedProject.status_board.status_board"
        />
        <p
          v-else
          class="caption text--secondary font-italic mb-0"
        >Sin estado definido.</p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code,
  Paragraph, BulletList, OrderedList, ListItem, Link,
  Blockquote, HardBreak, HorizontalRule, History
} from 'tiptap-vuetify'
import { updateProjectBoard } from '@/helpers/api/project'

export default {
  name: 'ProjectStatusCard',
  components: { TiptapVuetify },
  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    storagePercent() {
      const size = this.$store.fileManagerStore.state.projectStorageInfo.total_size || 0
      return (size * 100) / 10737418240
    }
  },
  data: () => ({
    loading: false,
    projectStatus: '',
    editingProjectBoard: false,
    extensions: [
      History, Blockquote, Link, Underline, Strike, Italic,
      ListItem, BulletList, OrderedList,
      [Heading, { options: { levels: [1, 2, 3] } }],
      Bold, Code, HorizontalRule, Paragraph, HardBreak
    ]
  }),
  methods: {
    getProgressChartData(weeklyProgress) {
      return {
        labels: weeklyProgress.data.map(d => 'S' + d.week),
        datasets: [
          {
            label: 'Plan',
            borderColor: '#2D4B95',
            backgroundColor: 'transparent',
            data: weeklyProgress.data.map(d => d.planned),
            fill: false,
            pointRadius: 0,
            borderWidth: 1.5,
            lineTension: 0.4
          },
          {
            label: 'Real',
            borderColor: '#34BFA3',
            backgroundColor: 'rgba(52,191,163,0.1)',
            data: weeklyProgress.data.map(d => d.actual),
            fill: false,
            pointRadius: 2,
            pointBackgroundColor: '#34BFA3',
            borderWidth: 1.5,
            lineTension: 0.4,
            spanGaps: false
          },
          {
            label: 'Reprogramación',
            borderColor: '#EAB261',
            borderDash: [4, 4],
            backgroundColor: 'transparent',
            data: weeklyProgress.data.map(d => d.reforecast),
            fill: false,
            pointRadius: 0,
            borderWidth: 1.5,
            lineTension: 0.4,
            spanGaps: false
          }
        ]
      }
    },
    getProgressChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
          align: 'end',
          labels: { fontSize: 8, boxWidth: 8, padding: 5, usePointStyle: true }
        },
        tooltips: { mode: 'index', intersect: false },
        scales: {
          xAxes: [{
            gridLines: { display: false },
            ticks: { fontSize: 8, fontColor: '#8a9bbd', maxRotation: 0 }
          }],
          yAxes: [{
            ticks: {
              min: 0,
              max: 100,
              stepSize: 25,
              fontSize: 8,
              fontColor: '#8a9bbd',
              callback: (v) => v + '%'
            },
            gridLines: { color: 'rgba(138,155,189,0.15)' }
          }]
        }
      }
    },
    editProjectBoard() {
      if (this.selectedProject?.status_board) {
        this.projectStatus = this.selectedProject.status_board.status_board
      }
      this.editingProjectBoard = true
    },
    async updateBoard() {
      this.loading = true
      const data = {
        project_id: this.selectedProject.id,
        status_board: this.projectStatus
      }
      const board = await updateProjectBoard(data)
      if (board.code === 200) {
        const project = { ...this.selectedProject, status_board: board.status }
        this.$store.projectsStore.commit('setSelectedProject', project)
      }
      this.editingProjectBoard = false
      this.loading = false
    }
  }
}
</script>

<style scoped>
.project-status-card {
  border: 1px solid #E0E0E0;
  border-radius: 12px !important;
  overflow: hidden;
}
.status-avance-section {
  padding: 14px 16px 10px;
  border-bottom: 1px solid rgba(45,75,149,0.08);
}
.status-project-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.status-project-name {
  font-size: 13px;
  font-weight: 700;
  color: #2D4B95;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.module-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.module-title {
  font-size: 12px;
  font-weight: 700;
  color: #2D4B95;
  letter-spacing: 0.2px;
}
.cut-week-badge {
  font-size: 9px;
  font-weight: 600;
  color: #8a9bbd;
  background: #f0f2f8;
  padding: 2px 7px;
  border-radius: 10px;
}
.progress-chart-wrapper {
  height: 180px;
  position: relative;
  overflow: hidden;
}
.text-primary { color: #2D4B95; }
</style>

<style>
#edit-project-status .tiptap-vuetify-editor__content {
  min-height: 150px;
  padding: 0;
}
#edit-project-status .tiptap-vuetify-editor__content .ProseMirror {
  padding: 15px;
  margin: 0 !important;
  min-height: 150px;
}
#edit-project-status .tiptap-vuetify-editor__content .ProseMirror p {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}
.status-board-info p { margin-bottom: 0; }
.status-board-info table { width: 100%; border-collapse: collapse; margin: 5px 0; }
.status-board-info table td { border: 1px solid grey; padding: 4px; }
</style>
